import React, { useState } from 'react'
import { parsePhoneNumber } from 'libphonenumber-js'
import { Typography } from '@telavita-core/react-design-kit'
import { getDay, format } from 'date-fns'

import ModalGeneric from 'containers/ModalManager/Modals/ModalGeneric'
import { InitialContent, RescheduleTotalRecurrentReservationContent, SuccessContent } from './components'

import { Reservation } from 'models/Reservation'
import { WEEKDAYS_NAMES } from 'settings/dayPicker'
import { usePatientScheduleList } from 'hooks'

interface RescheduleTotalRecurrentReservationModalProps {
  handleClose: () => void
  onRefresh: () => void
  reservation: Reservation
}

interface RescheduleSuccessData {
  newRecurrentStartDate: Date,
  exceptions: { oldDate: string, newDate: string }[]
}

enum ModalStatuses {
  INITIAL = 'INITIAL',
  RESCHEDULE = 'RESCHEDULE',
  SUCCESS = 'SUCCESS'
}

function formatPhone(phone: string) {
  return isNaN(Number(phone)) 
    ? '' 
    : parsePhoneNumber(`+${phone}`).formatNational()
}

function getModalTitleByModalStatus(
  modalStatus: ModalStatuses,
) {
  return modalStatus === ModalStatuses.SUCCESS
    ? ''
    : <>Remarcar toda a reserva</>
}

function getModalSubtitleByModalStatus(
  patientFullName: string,
  modalStatus: ModalStatuses,
  reservationDate: string
) {
  return modalStatus === ModalStatuses.SUCCESS
    ? ''
    : (
      <>
        Paciente: <strong>{patientFullName}</strong>
        <br />
        <Typography variant='content2' color='danger' center>
          Reserva atual <strong>{reservationDate}</strong>
        </Typography>
      </>
    )
}

export const RescheduleTotalRecurrentReservationModal: React.FC<RescheduleTotalRecurrentReservationModalProps> = ({
  handleClose,
  onRefresh,
  reservation
}) => {
  const [modalStatus, setModalStatus] = useState<ModalStatuses>(ModalStatuses.INITIAL)
  const [rescheduleSuccessData, setRescheduleSuccessData] = useState<RescheduleSuccessData>(null)
  
  const {
    patientUsername,
    patientFullName, 
    patientCellphone, 
    startDate 
  } = reservation
  
  const reservationWeekdayName = WEEKDAYS_NAMES[getDay(startDate)]
  const reservationHour = format(startDate, 'HH')
  const recurrentReservationDate = `${reservationWeekdayName}, ${reservationHour}h`

  const patientPhone = formatPhone(patientCellphone)
  const [patientFistName] = patientFullName.split(' ')
  const modalTitle = getModalTitleByModalStatus(modalStatus)
  const modalSubtitle = getModalSubtitleByModalStatus(patientFullName, modalStatus, recurrentReservationDate)

  const { patientScheduleList, isFetching: isFetchingPatientScheduleList } = usePatientScheduleList({
    patientUsername,
    removeParticularSchedules: false
  })

  function handleContinueReschedule() {
    setModalStatus(ModalStatuses.RESCHEDULE)
  }

  function handleCloseModal() {
    if (modalStatus === ModalStatuses.SUCCESS) {
      onRefresh()
    }
    
    setModalStatus(ModalStatuses.INITIAL)
    handleClose()
  }

  function handleSuccess(rescheduleSuccessData: RescheduleSuccessData) {
    setModalStatus(ModalStatuses.SUCCESS)
    setRescheduleSuccessData(rescheduleSuccessData)
  }

  return (
    <ModalGeneric modalTitle={modalTitle} modalSubtitle={modalSubtitle} onCloseModal={handleCloseModal}>
      {modalStatus === ModalStatuses.INITIAL && (
        <InitialContent
          isFetchingPatientScheduleList={isFetchingPatientScheduleList}
          patientPhone={patientPhone}
          patientFirstName={patientFistName}
          totalSchedules={patientScheduleList?.length}
          handleContinueReschedule={handleContinueReschedule} 
        />
      )}

      {modalStatus === ModalStatuses.RESCHEDULE && (
        <RescheduleTotalRecurrentReservationContent 
          reservation={reservation} 
          handleSuccess={handleSuccess} 
        />
      )}

      {modalStatus === ModalStatuses.SUCCESS && (
        <SuccessContent 
          handleClose={handleCloseModal} 
          patientFirstName={patientFistName}
          {...rescheduleSuccessData}
        />
      )}
    </ModalGeneric>
  )
}